"use client";

import React from "react";
import { Paperclip, Trash2, Loader2, Check, Download } from "lucide-react";
import { Button, Progress, cn, Badge } from "visuais";
import { useCompactAttachmentsDialog } from "./_hook";

interface LocalAnexo {
  item: string;
  nomeArquivo: string;
  observacao: string;
  isNew?: boolean;
}

interface AnexoListProps {
  anexos: LocalAnexo[];
}

export function AnexoList({ anexos }: AnexoListProps) {
  const {
    handleDownload,
    handleDelete,
    handleUpdateDescription,
    getFileProgress,
    getConnectionStatus,
  } = useCompactAttachmentsDialog();

  if (anexos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-20 text-muted-foreground/50">
        <Paperclip className="w-6 h-6 mb-1 opacity-30" />
        <span className="text-xs">Nenhum anexo</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {anexos.map((anexo) => {
        const fileProgress = getFileProgress(anexo);
        const connectionStatus = getConnectionStatus(anexo);
        const progress = fileProgress?.progress || 0;
        const isUploading = fileProgress?.status === "uploading";
        const isCompleted = fileProgress?.status === "completed";

        return (
          <div
            key={anexo.item}
            className={cn(
              "flex items-center gap-2 p-2 transition-all relative",
              isUploading && "border-blue-500/50 bg-blue-500/10 shadow-sm",
              isCompleted && "border-green-500/50 bg-green-500/10 shadow-sm",
              !isUploading && !isCompleted && "border-border hover:shadow-sm"
            )}
          >
            {/* Ícone de status */}
            <div className="flex-shrink-0">
              {isUploading ? (
                <Loader2 className="w-3 h-3 animate-spin text-blue-400" />
              ) : isCompleted ? (
                <Check className="w-3 h-3 text-green-400" />
              ) : (
                <Paperclip className="w-3 h-3 text-muted-foreground" />
              )}
            </div>

            {/* Nome do arquivo */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <p
                  className="text-xs font-medium truncate"
                  title={anexo.nomeArquivo}
                >
                  {anexo.nomeArquivo}
                </p>
                {anexo.isNew && (
                  <Badge
                    variant="secondary"
                    className="text-[9px] px-1 py-0 ml-1"
                  >
                    Novo
                  </Badge>
                )}
              </div>
              {anexo.observacao && (
                <p
                  className="text-[10px] text-muted-foreground truncate mt-0.5"
                  title={anexo.observacao}
                >
                  {anexo.observacao}
                </p>
              )}
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[9px] text-muted-foreground/60">
                  Item: {anexo.item}
                </span>
                <span
                  className={cn(
                    "text-[9px] px-1 py-0.5 rounded",
                    isCompleted && "text-green-400 bg-green-500/20",
                    isUploading && "text-blue-400 bg-blue-500/20",
                    !isUploading &&
                      !isCompleted &&
                      "text-amber-400 bg-amber-500/20"
                  )}
                >
                  {connectionStatus.text}
                </span>
              </div>
            </div>

            {/* Ações */}
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDownload(anexo.item)}
                className="h-6 w-6 p-0 hover:bg-primary/10 hover:text-primary"
                title="Download"
              >
                <Download className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDelete(anexo.item)}
                disabled={isUploading}
                className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                title={
                  isUploading ? "Aguarde o upload terminar" : "Remover anexo"
                }
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>

            {/* Progress bar */}
            {fileProgress && isUploading && (
              <div className="absolute bottom-0 left-0 right-0">
                <Progress
                  value={progress}
                  className="h-1"
                  // @ts-expect-error - Progress component accepts these props
                  indicatorClassName="bg-blue-500"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
